import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { Storage, Utils } from '../../config';
import { SUPPORT_LIMITS } from '../../config/constants';
import { AppAction, MsgAction } from '../../store/actions';
import './styles.css';
import { Loader, Modal } from '../../components';
import Otp from '../../components/Otp';
import { useForm, Controller } from 'react-hook-form';
import { useShake } from '../../hooks/useShake';

// REACT ICONS
import { logoImage, warningImage, LimageImage } from '../../assets/images';
import { generateFCMToken } from '../../config/firebase/firebase';

/**
 * Sign-in.
 *
 * Two panes: a full-bleed brand panel and the form column. Everything below is
 * presentation — the login, 2FA-challenge and contact-support flows are
 * unchanged.
 */

/* Eye toggle for the password field. The old PNG pair was a fixed dark glyph,
   which disappeared against the dark surface; `currentColor` follows the token. */
const EyeIcon = ({ off }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M2.5 12S6 5.8 12 5.8 21.5 12 21.5 12S18 18.2 12 18.2 2.5 12 2.5 12z" />
    <circle cx="12" cy="12" r="3" />
    {off && <path d="m4 20 16-16" />}
  </svg>
);

/**
 * Layout for this screen lives in styles.css, keyed on `.signin-page`,
 * `.signin-brand` and `.signin-form-side`.
 *
 * It has to: an inline style beats a stylesheet, so any property a media query
 * needs to change cannot be declared here. `.signin-brand { display: none }`
 * was already in the stylesheet and had never once applied, because the object
 * below set `display: flex` inline — which is why the decorative panel stayed
 * on screen at every width and squeezed the sign-in form into half a phone.
 *
 * What is left here is what does not vary: colour, type and spacing INSIDE the
 * form column.
 */
const styles = {
  formCol: {
    width: '100%',
    maxWidth: 396,
    display: 'flex',
    flexDirection: 'column',
    gap: 22,
  },
  field: { display: 'flex', flexDirection: 'column', gap: 7 },
  label: { fontSize: 12.5, fontWeight: 700, color: 'var(--muted)' },
  input: {
    height: 50,
    width: '100%',
    padding: '0 15px',
    borderRadius: 'var(--r-lg)',
    border: '1.5px solid var(--border)',
    background: 'var(--panel-2)',
    color: 'var(--text)',
    fontSize: 14.5,
    outline: 'none',
  },
  primaryBtn: {
    height: 50,
    border: 0,
    borderRadius: 'var(--r-lg)',
    background: 'var(--accent)',
    color: 'var(--accent-fg)',
    fontSize: 14.5,
    fontWeight: 800,
    letterSpacing: '-0.01em',
    cursor: 'pointer',
  },
  metaRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 12,
    fontSize: 13,
    fontWeight: 600,
  },
  error: { fontSize: 12, fontWeight: 600, color: 'var(--danger)' },
  reassure: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '13px 15px',
    borderRadius: 'var(--r-lg)',
    background: 'var(--panel-2)',
    border: '1px solid var(--border)',
  },
  reassureGlyph: {
    width: 30,
    height: 30,
    flex: '0 0 auto',
    borderRadius: 'var(--r-md)',
    background: 'var(--accent-soft)',
    color: 'var(--accent-strong)',
    display: 'grid',
    placeItems: 'center',
  },
  modalInput: {
    height: 44,
    width: '100%',
    padding: '0 14px',
    borderRadius: 'var(--r-md)',
    border: '1.5px solid var(--border)',
    background: 'var(--panel-2)',
    color: 'var(--text)',
    fontSize: 14,
    outline: 'none',
  },
  /* Error on the left, character counter on the right, so a field can show both
     without the counter jumping as the message appears and clears. */
  fieldFoot: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: 10,
    minHeight: 15,
  },
  counter: {
    marginLeft: 'auto',
    fontSize: 11.5,
    fontWeight: 600,
    // Tabular figures: without them the row twitches sideways on every keystroke
    // that changes the digit widths.
    fontVariantNumeric: 'tabular-nums',
  },
};

const SignIn = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loader = useSelector((state) => state?.AppReducer.loader);
  // Bumped by SIGNIN_FAILURE / VERIFY_2FA_FAILURE. The rejection was previously
  // reported only by a toast in the far corner of the screen, nowhere near the
  // field that caused it.
  const authRejections = useSelector((state) => state?.AppReducer?.authRejections);
  const { shakeProps } = useShake(authRejections);
  const email = 'info@synapse.com';
  const [showPassword, setShowPassword] = useState(false);
  // 2FA challenge step: set once login returns mfa_required.
  const [mfaToken, setMfaToken] = useState(null);
  const [code, setCode] = useState('');
  const [useBackup, setUseBackup] = useState(false);
  const [modalOpen, setModal] = useState(false);
  const [contactSupportModalOpen, setContactSupportModalOpen] = useState(false);
  const [contactSupportLoader, setContactSupportLoader] = useState(false);

  /**
   * Covers the window BEFORE the login action is dispatched.
   *
   * `LoginForm` awaits the FCM token first, and redux `loader` only flips once
   * SIGNIN is dispatched — so for however long the push round-trip takes (and it
   * can sit indefinitely on the browser's permission prompt) the click produced
   * no feedback at all. Handover is seamless: the reducer has already set
   * `loader` by the time this clears.
   */
  const [submitting, setSubmitting] = useState(false);
  const busy = submitting || loader;

  useEffect(() => {
    // generateFCMToken()
  }, []);

  const { handleSubmit, control } = useForm({
    mode: 'onChange',
  });

  // Contact Support Form. Empty-string defaults keep every field controlled, so
  // the message counter has a length to read on first render and `reset()` puts
  // the inputs back to blank rather than to `undefined`.
  const {
    handleSubmit: handleSubmitContactSupport,
    control: controlContactSupport,
    reset: resetContactSupport,
  } = useForm({
    mode: 'onChange',
    defaultValues: { email: '', login_id: '', message: '' },
  });

  const LoginForm = async (data) => {
    if (!data) return;

    setSubmitting(true);

    // FCM is best-effort: fetch the push token if notifications are granted, but
    // never block login on it. Users who denied notifications still sign in;
    // push simply stays off until they enable it. The device is registered against
    // this user right after the session is persisted (AppMiddleware) — this token
    // only feeds auth's session record and the legacy users.fcm_token column.
    let token = null;
    try {
      token = await generateFCMToken();
    } catch {
      token = null;
    }

    const payloadData = {
      email: '',
      login_id: data?.loginID,
      password: data?.password,
      platform: 'WEB',
      fcm_token: token || null,
    };

    dispatch(
      AppAction.SignIn(payloadData, (data) => {
        // 2FA enabled → no session yet; switch to the code-entry step.
        if (data?.mfaRequired) {
          setMfaToken(data.mfaToken);
          setCode('');
          setUseBackup(false);
          return;
        }
        onAuthenticated(data, token);
      })
    );

    // The SIGNIN reducer case set `loader` synchronously during that dispatch, so
    // dropping the local flag here does not leave a gap — and SIGNIN_FAILURE is
    // what clears the button on a rejected login, which a local flag would miss.
    setSubmitting(false);
  };

  // Shared post-authentication handler (password login OR 2FA verify).
  const onAuthenticated = (data, token) => {
    dispatch(
      MsgAction.ToggleNotifications(
        1,
        () => {
          dispatch(AppAction.UserProfile());
        },
        token || null
      )
    );
    if (data?.data?.status === 0) {
      // A forced password change outranks any deep link. The pending redirect is left in
      // place deliberately: it survives sessionStorage across the password change, so the
      // link the user originally clicked still resolves once they are through the gate.
      navigate('/change-password');
      return;
    }

    /**
     * Go where they were trying to go, or to messages.
     *
     * This used to be an unconditional `navigate('/messages')`, which silently discarded
     * every deep link: `AuthRoutes` bounces an unauthenticated visitor to `/`, and by the
     * time they signed in the destination was gone. A note shared with them, a
     * conversation URL, a notification opened on a cold start — all landed on the chat
     * list instead, with nothing to say the thing they clicked existed.
     *
     * `takePostLoginRedirect` consumes the value and refuses anything that is not a
     * same-origin path, so this cannot be turned into an open redirect.
     */
    navigate(Storage.takePostLoginRedirect() ?? '/messages');
  };

  // Complete the 2FA challenge with the entered code (TOTP or backup code).
  const VerifyCode = () => {
    if (!code || (!useBackup && code.length < 6)) {
      Utils.showMessage('error', 'Enter the 6-digit code from your authenticator app');
      return;
    }
    dispatch(
      AppAction.Verify2fa({ mfaToken, code }, (data) => {
        onAuthenticated(data, null);
      })
    );
  };

  const CancelMfa = () => {
    setMfaToken(null);
    setCode('');
    setUseBackup(false);
  };

  const ModalHandlerCancel = () => {
    setModal(false);
  };
  const ModalHandler = () => {
    setModal(true);
  };

  const ContactSupportModalOpen = () => {
    setContactSupportModalOpen(true);
  };

  const ContactSupportModalCancel = () => {
    setContactSupportModalOpen(false);
    resetContactSupport();
  };

  const handleContactSupportSubmit = async (data) => {
    // Validate that at least email or login_id is provided
    if (
      (!data?.email || data?.email.trim() === '') &&
      (!data?.login_id || data?.login_id.toString().trim() === '')
    ) {
      Utils.showMessage('error', 'Please provide either email or login ID');
      return;
    }

    setContactSupportLoader(true);
    const payload = {
      email: data?.email || '',
      login_id: data?.login_id ? data?.login_id.toString() : '',
      message: data?.message,
    };

    dispatch(
      AppAction.ContactSupport(payload, (response) => {
        setContactSupportLoader(false);
        // The saga passes the API envelope ({ statusCode, success, message }),
        // not the axios response — check `success`, not the absent `status`.
        if (response?.success) {
          Utils.showMessage('success', response?.message || 'Message sent to support successfully');
          setContactSupportModalOpen(false);
          resetContactSupport();
        }
        // No toast on failure: the axios interceptor already raised one carrying
        // the server's own reason ("User account not found.", a rate-limit
        // refusal, and so on). Toasting again here stacked a second, vaguer copy
        // of the same event. The modal stays open with the values intact so the
        // ID or email can be corrected and re-sent.
      })
    );
  };

  return (
    <div className="signin-page">
      {/* ---------------- brand panel (wide viewports only) ---------------- */}
      <div className="signin-brand">
        <img className="signin-brand-mark" src={logoImage} alt="Synapse" />

        <div className="signin-brand-art">
          <img src={LimageImage} alt="" />
        </div>

        <div className="signin-brand-copy">
          <div className="signin-brand-headline">The Most Trusted And Fast messaging Ever</div>
          <div className="signin-brand-sub">
            Leader for Cryptocurrency Tracking and Tax Reporting{' '}
          </div>
        </div>
      </div>

      {/* ---------------- form column ---------------- */}
      <div className="signin-form-side">
        <div style={styles.formCol}>
          <img className="signin-form-mark" src={logoImage} alt="Synapse" />

          {!mfaToken && (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                <div
                  style={{
                    fontSize: 28,
                    fontWeight: 800,
                    letterSpacing: '-0.035em',
                    color: 'var(--text)',
                  }}
                >
                  Welcome to Synapse
                </div>
                <div style={{ fontSize: 14, color: 'var(--muted)' }}>Login to your account</div>
              </div>

              <form
                onSubmit={handleSubmit(LoginForm)}
                style={{ display: 'flex', flexDirection: 'column', gap: 22 }}
                // Shakes on a rejected login. Either field can be the wrong one
                // and the server never says which, so the whole form answers.
                {...shakeProps}
              >
                <Controller
                  name="loginID"
                  control={control}
                  rules={{
                    required: 'This field is required',
                    validate: (value) => value.toString().length !== 0 || 'This field is required',
                  }}
                  render={({ field, fieldState }) => (
                    <label style={styles.field}>
                      <span style={styles.label}>Login ID</span>
                      <input
                        required
                        type="number"
                        placeholder="Enter Your Login ID"
                        style={{ ...styles.input, fontFamily: 'var(--font-mono)' }}
                        onKeyDown={(e) => {
                          if (e.key === 'e' || e.key === 'E' || e.key === '-' || e.key === '+') {
                            e.preventDefault();
                          }
                        }}
                        {...field}
                      />
                      {fieldState.error && (
                        <span style={styles.error}>{fieldState.error.message}</span>
                      )}
                    </label>
                  )}
                />

                <Controller
                  name="password"
                  control={control}
                  rules={{
                    required: 'This field is required',
                    validate: (value) => value.toString().length !== 0 || 'This field is required',
                  }}
                  render={({ field, fieldState }) => (
                    <label style={styles.field}>
                      <span style={styles.label}>Password</span>
                      <span style={{ position: 'relative', display: 'flex' }}>
                        <input
                          required
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Enter Your Password"
                          style={{ ...styles.input, paddingRight: 46 }}
                          {...field}
                        />
                        <button
                          type="button"
                          className="signin-eye"
                          title={showPassword ? 'Hide password' : 'Show password'}
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          <EyeIcon off={showPassword} />
                        </button>
                      </span>
                      {fieldState.error && (
                        <span style={styles.error}>{fieldState.error.message}</span>
                      )}
                    </label>
                  )}
                />

                <div style={styles.metaRow}>
                  <span
                    role="button"
                    tabIndex={0}
                    className="cursor-pointer"
                    style={{ color: 'var(--accent-strong)' }}
                    onClick={ModalHandler}
                    onKeyDown={(e) => e.key === 'Enter' && ModalHandler()}
                  >
                    Forgot Password?
                  </span>
                  <span
                    role="button"
                    tabIndex={0}
                    className="cursor-pointer"
                    style={{ color: 'var(--muted)' }}
                    onClick={ContactSupportModalOpen}
                    onKeyDown={(e) => e.key === 'Enter' && ContactSupportModalOpen()}
                  >
                    Contact Support
                  </span>
                </div>

                <button disabled={busy} type="submit" style={styles.primaryBtn}>
                  {busy ? (
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 8,
                      }}
                    >
                      <Loader.Circular size={'medium'} color="currentColor" />
                      Signing in…
                    </span>
                  ) : (
                    'Sign In'
                  )}
                </button>
              </form>
            </>
          )}

          {mfaToken && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                <div
                  style={{
                    fontSize: 26,
                    fontWeight: 800,
                    letterSpacing: '-0.03em',
                    color: 'var(--text)',
                  }}
                >
                  Two-factor authentication
                </div>
                <div style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.6 }}>
                  {useBackup
                    ? 'Enter one of your backup codes.'
                    : 'Enter the 6-digit code from your authenticator app.'}
                </div>
              </div>

              {/* Wrapper exists so a rejected code shakes the boxes themselves
                  rather than the whole step, heading and button included. */}
              <div {...shakeProps}>
                {useBackup ? (
                  <input
                    autoFocus
                    type="text"
                    value={code}
                    placeholder="Backup code"
                    onChange={(e) => setCode(e.target.value.trim())}
                    style={{
                      ...styles.input,
                      fontFamily: 'var(--font-mono)',
                      letterSpacing: '0.12em',
                    }}
                  />
                ) : (
                  <Otp
                    value={code}
                    onChange={setCode}
                    numInputs={6}
                    inputType="number"
                    inputStyle={{
                      flex: 1,
                      width: '100%',
                      // Without this a flex item cannot shrink past its content
                      // width, so six boxes plus their gaps overflowed the column
                      // on a narrow phone instead of dividing it.
                      minWidth: 0,
                      height: '56px',
                      borderRadius: 'var(--r-lg)',
                      border: '1.5px solid var(--border)',
                      background: 'var(--panel-2)',
                      color: 'var(--text)',
                      fontFamily: 'var(--font-mono)',
                      fontSize: '20px',
                      fontWeight: 700,
                      textAlign: 'center',
                      outline: 'none',
                    }}
                    containerStyle={{ display: 'flex', gap: '9px' }}
                  />
                )}
              </div>

              <button
                type="button"
                disabled={loader}
                onClick={VerifyCode}
                style={styles.primaryBtn}
              >
                {loader ? (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                    }}
                  >
                    <Loader.Circular size={'medium'} color="currentColor" />
                    Verify
                  </span>
                ) : (
                  'Verify and continue'
                )}
              </button>

              <div style={styles.metaRow}>
                <span
                  role="button"
                  tabIndex={0}
                  className="cursor-pointer"
                  style={{ color: 'var(--accent-strong)' }}
                  onClick={() => {
                    setUseBackup((v) => !v);
                    setCode('');
                  }}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter') return;
                    setUseBackup((v) => !v);
                    setCode('');
                  }}
                >
                  {useBackup ? 'Use authenticator code' : 'Use a backup code'}
                </span>
                <span
                  role="button"
                  tabIndex={0}
                  className="cursor-pointer"
                  style={{ color: 'var(--muted)' }}
                  onClick={CancelMfa}
                  onKeyDown={(e) => e.key === 'Enter' && CancelMfa()}
                >
                  Back to login
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ---------------- forgot password ---------------- */}
      <Modal.CustomModal
        open={modalOpen}
        cancelModal={ModalHandlerCancel}
        centered={true}
        width={440}
        Children={
          <div
            style={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 14,
              padding: '8px 0',
              textAlign: 'center',
            }}
          >
            <img
              src={warningImage}
              alt=""
              style={{ width: 52, height: 52, objectFit: 'contain' }}
            />
            <div style={{ fontSize: 13.5, lineHeight: 1.7, color: 'var(--muted)' }}>
              {"If you've forgotten your password, please contact a representative of "}
              <span style={{ fontWeight: 700, color: 'var(--text)' }}>Synapse</span>
              {' with your Login ID.'} <br></br> {' You can send email to '}
              <a href={`mailto:${email}`}>{email}</a>
            </div>
          </div>
        }
      />

      {/* ---------------- contact support ---------------- */}
      <Modal.CustomModal
        open={contactSupportModalOpen}
        cancelModal={ContactSupportModalCancel}
        centered={true}
        width={480}
        Children={
          <div style={{ width: '100%' }}>
            <div className="modal-header">
              <div className="modal-header-text">
                <div className="modal-title">Contact support</div>
                <div className="modal-subtitle">
                  Give us an email or your login ID and tell us what went wrong.
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmitContactSupport(handleContactSupportSubmit)}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
                <Controller
                  name="email"
                  control={controlContactSupport}
                  rules={{
                    maxLength: {
                      value: SUPPORT_LIMITS.email.max,
                      message: `Email must be at most ${SUPPORT_LIMITS.email.max} characters`,
                    },
                    validate: (value) => {
                      if (!value || value.trim() === '') {
                        return true; // Allow empty/blank
                      }
                      const emailPattern = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
                      return emailPattern.test(value) || 'Invalid email address';
                    },
                  }}
                  render={({ field, fieldState }) => (
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>
                        Email
                      </span>
                      <input
                        type="email"
                        placeholder="you@company.com"
                        maxLength={SUPPORT_LIMITS.email.max}
                        style={styles.modalInput}
                        {...field}
                      />
                      {fieldState.error && (
                        <span style={styles.error}>{fieldState.error.message}</span>
                      )}
                    </label>
                  )}
                />

                <Controller
                  name="login_id"
                  control={controlContactSupport}
                  rules={{
                    validate: (value) => {
                      const raw = (value ?? '').toString().trim();
                      if (raw === '') {
                        return true; // Allow empty
                      }
                      const numPattern = /^[0-9]+$/;
                      if (!numPattern.test(raw)) return 'Login ID must be a number';
                      return (
                        raw.length <= SUPPORT_LIMITS.loginId.max ||
                        `Login ID must be at most ${SUPPORT_LIMITS.loginId.max} digits`
                      );
                    },
                  }}
                  render={({ field, fieldState }) => (
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>
                        Login ID
                      </span>
                      <input
                        type="number"
                        placeholder="Enter your login ID"
                        style={{ ...styles.modalInput, fontFamily: 'var(--font-mono)' }}
                        onKeyDown={(e) => {
                          if (e.key === 'e' || e.key === 'E' || e.key === '-' || e.key === '+') {
                            e.preventDefault();
                          }
                        }}
                        {...field}
                        value={field.value ?? ''}
                        // `maxLength` is inert on a number input, so the digit cap
                        // is applied here — which also catches a paste that never
                        // went through the keydown guard above.
                        onChange={(e) =>
                          field.onChange(
                            e.target.value.replace(/\D/g, '').slice(0, SUPPORT_LIMITS.loginId.max)
                          )
                        }
                      />
                      {fieldState.error && (
                        <span style={styles.error}>{fieldState.error.message}</span>
                      )}
                    </label>
                  )}
                />

                <Controller
                  name="message"
                  control={controlContactSupport}
                  rules={{
                    required: 'Message is required',
                    maxLength: {
                      value: SUPPORT_LIMITS.message.max,
                      message: `Message must be at most ${SUPPORT_LIMITS.message.max} characters`,
                    },
                    // Counted on the trimmed value: the backend trims before its
                    // own empty check, so ten spaces used to pass here and come
                    // back a 400.
                    validate: (value) =>
                      (value ?? '').trim().length >= SUPPORT_LIMITS.message.min ||
                      `Message must be at least ${SUPPORT_LIMITS.message.min} characters`,
                  }}
                  render={({ field, fieldState }) => {
                    const used = (field.value ?? '').length;
                    const atLimit = used >= SUPPORT_LIMITS.message.max;

                    return (
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>
                          Message
                        </span>
                        <textarea
                          rows="5"
                          placeholder="Tell us what went wrong."
                          maxLength={SUPPORT_LIMITS.message.max}
                          aria-describedby="support-message-counter"
                          style={{
                            ...styles.modalInput,
                            height: 'auto',
                            padding: '13px 14px',
                            lineHeight: 1.6,
                            resize: 'vertical',
                          }}
                          {...field}
                          value={field.value ?? ''}
                        />
                        <div style={styles.fieldFoot}>
                          {fieldState.error && (
                            <span style={styles.error}>{fieldState.error.message}</span>
                          )}
                          <span
                            id="support-message-counter"
                            aria-live="polite"
                            style={{
                              ...styles.counter,
                              color: atLimit ? 'var(--danger)' : 'var(--muted)',
                            }}
                          >
                            {used} / {SUPPORT_LIMITS.message.max}
                          </span>
                        </div>
                      </label>
                    );
                  }}
                />
              </div>

              <div className="modal-footer">
                <div className="modal-footer-actions">
                  <button
                    type="button"
                    className="modal-btn is-ghost"
                    onClick={ContactSupportModalCancel}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={contactSupportLoader}
                    className="modal-btn is-accent"
                  >
                    {contactSupportLoader ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <Loader.Circular size={'small'} color="currentColor" />
                        Sending…
                      </span>
                    ) : (
                      'Send'
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        }
      />
    </div>
  );
};

export default SignIn;
